import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { useSeo } from "@/hooks/useSeo";

const FORM_ID = "db7hotn3hiaolq192hfg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db7hotn3hiaolq192hfg";

export default function About() {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  useSeo({
    title: "关于我们 - 黑桃视频影视聚合平台",
    description:
      "了解黑桃视频的平台定位、宗旨与内容范围。黑桃视频专注最新影视资讯与影视频道推荐，并提供联系方式与反馈入口，欢迎与我们交流。",
    keywords: "关于黑桃视频,影视聚合平台,联系我们,影视资讯平台,意见反馈",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");

    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setSuccess(true);
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "website_alt") return;
      body.append(key, String(value));
    });

    setSubmitting(true);
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (!ok || isSpam) {
        setFormError(serverMsg || "提交失败，请稍后重试");
        return;
      }

      setSuccess(true);
      form.reset();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "网络异常，提交失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="relative h-[240px] w-full overflow-hidden md:h-[320px]">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20modern%20studio%20workspace%20with%20warm%20amber%20accent%20lighting%2C%20minimal%20elegant%20interior%2C%20professional%20creative%20atmosphere%2C%20wide%20banner&width=1600&height=640&seq=ht-about-hero&orientation=landscape"
          alt="黑桃视频团队介绍主图"
          title="关于黑桃视频 - 影视聚合平台"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-background-950/30"></div>
        <div className="absolute inset-0 flex w-full items-end">
          <div className="mx-auto w-full max-w-[1440px] px-4 pb-8 md:px-8 md:pb-12">
            <h1 className="font-heading text-2xl font-700 tracking-wide text-background-950 md:text-4xl">
              关于黑桃视频
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-foreground-800 md:text-base">
              专业、实用且内容丰富的影视内容聚合平台，陪你发现下一部想看的好作品。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="space-y-10">
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-600 text-foreground-950">
                <span className="flex h-6 w-6 items-center justify-center text-primary-500">
                  <i className="ri-focus-3-line text-[20px]" aria-hidden="true"></i>
                </span>
                网站定位与使命
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                黑桃视频致力于成为影视爱好者获取信息的第一站。我们聚焦最新电影、电视剧资讯与热门影视频道推荐，
                通过清晰的分类结构与严谨的内容整理，帮助你在海量作品中快速定位感兴趣的内容。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                我们的使命是：让影视资讯更易获取，让好内容更容易被发现。
              </p>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-600 text-foreground-950">
                <span className="flex h-6 w-6 items-center justify-center text-accent-500">
                  <i className="ri-stack-line text-[20px]" aria-hidden="true"></i>
                </span>
                内容范围说明
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
                <li className="flex gap-2.5">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                  <span><strong className="font-500 text-foreground-900">影视资讯</strong>：按时间顺序更新的电影、电视剧动态与追更提醒。</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                  <span><strong className="font-500 text-foreground-900">影视分类</strong>：电影、电视剧、综艺、纪录片四大类，支持地区、年份与类型筛选。</span>
                </li>
                <li className="flex gap-2.5">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                  <span><strong className="font-500 text-foreground-900">频道推荐</strong>：聚合优质影视频道，提供观看方向与专题导览。</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-600 text-foreground-950">
                <span className="flex h-6 w-6 items-center justify-center text-secondary-900">
                  <i className="ri-contacts-line text-[20px]" aria-hidden="true"></i>
                </span>
                联系方式
              </h2>
              <div className="mt-4 space-y-3 text-sm text-foreground-700">
                <p className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-primary-500">
                    <i className="ri-mail-line text-[18px]" aria-hidden="true"></i>
                  </span>
                  contact@heitaovideo.example
                </p>
                <p className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-primary-500">
                    <i className="ri-map-pin-line text-[18px]" aria-hidden="true"></i>
                  </span>
                  中国 · 北京市朝阳区影视文化产业园
                </p>
              </div>
            </section>
          </div>

          <div>
            <div className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl font-600 text-foreground-950">意见反馈</h2>
              <p className="mt-2 text-sm text-foreground-600">
                有任何建议或想推荐的内容，欢迎告诉我们，我们会认真阅读每一条反馈。
              </p>

              {formError && (
                <p className="mt-4 rounded-md border border-primary-500/40 bg-primary-500/10 px-3 py-2 text-xs text-primary-400">
                  {formError}
                </p>
              )}

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div>
                  <label htmlFor="feedback-name" className="mb-1.5 block text-xs text-foreground-600">昵称</label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    placeholder="请输入你的昵称"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-email" className="mb-1.5 block text-xs text-foreground-600">邮箱</label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    placeholder="用于接收我们的回复"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="feedback-topic" className="mb-1.5 block text-xs text-foreground-600">反馈类型</label>
                  <select
                    id="feedback-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="功能反馈">功能反馈</option>
                    <option value="频道推荐">频道推荐</option>
                    <option value="合作洽谈">合作洽谈</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="feedback-message" className="mb-1.5 block text-xs text-foreground-600">反馈内容</label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="请描述你的建议或想推荐的内容（500 字以内）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-guard-extra"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "提交中..." : "提交反馈"}
                </button>
              </form>
            </div>

            <div className="mt-6 rounded-xl border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-600 text-foreground-950">版权与免责声明</h2>
              <p className="mt-3 text-xs leading-6 text-foreground-600">
                本站为演示性质的内容聚合平台，站内所有影视资讯、作品信息、频道介绍与数据均为虚构示例，仅用于产品结构与界面设计展示。
                本站不提供任何视频播放、下载或资源存储服务，相关内容概不承担法律责任。
              </p>
              <p className="mt-2 text-xs leading-6 text-foreground-600">
                © 2026 黑桃视频 Heitao Video. 保留所有权利。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-center gap-3 rounded-xl border border-background-200 bg-background-100 p-8 text-center md:flex-row md:justify-between md:text-left">
          <p className="text-sm text-foreground-700">准备好开始探索了吗？</p>
          <div className="flex gap-3">
            <Link
              to="/category"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览影视分类
            </Link>
            <Link
              to="/channel"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-500 text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-500"
            >
              查看频道推荐
            </Link>
          </div>
        </div>
      </div>

      {success && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/70 px-4">
          <div className="w-full max-w-sm rounded-xl border border-background-200 bg-background-100 p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/15 text-primary-500">
              <i className="ri-checkbox-circle-fill text-[28px]" aria-hidden="true"></i>
            </span>
            <h3 className="mt-3 font-heading text-lg font-600 text-foreground-950">提交成功</h3>
            <p className="mt-2 text-sm text-foreground-600">感谢反馈，我们会尽快阅读你的建议。</p>
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              好的
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}